// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's smart guides (frontend-u0o.12): the inference
 * engine's (`inference.ts`) second tier, after SketchUp's "from point"
 * inference, Illustrator's smart guides and Revit's alignment lines.
 *
 *  - **Tracking points.** A point is *acquired* by resting the pointer on
 *    an endpoint, a midpoint or a guide point for `HOVER_MS` (`hoverTrack`;
 *    resting on it again drops it), a few at a time (`TRACK_CAP`). The
 *    tool also passes points of its own (`autoTrackPoints`: the ends of the
 *    walls being edited and their neighbours' far ends).
 *  - **Tracking lines** run from each point along the storey's two axes
 *    and along / square to the segment being drawn; a point lands where it
 *    is level with a tracking point, or where two tracking lines cross.
 *  - **Automatic hints** need no acquiring: a wall's axis **extended**
 *    past its ends, the endpoints of the walls **parallel** to the
 *    segment (square across from them — the right-angle connecting piece),
 *    **the same length** as a parallel wall, and **the same spacing** as
 *    two parallel walls (a third beyond them, or halfway between).
 *
 * Every hit comes with the dotted lines that explain it (`Track`) and a
 * Danish ScreenTip ("Fra endepunkt · Væg 3", "Forlængelse · Væg 3",
 * "Samme længde · Væg 4", "Samme afstand · Væg 1 og Væg 2"). The
 * priority against the first tier (endpoints, edges, the axis lock) is
 * `infer`'s: a point snap always wins, on an edge a tracking line only
 * chooses *where* on it, and on an axis lock a hint only chooses how far.
 * Pure: no React, no DOM.
 */

import type { XY } from './planWalls';

/** What a dotted line is, and so its colour (`TRACK_TOKEN`). */
export type TrackKind = 'axis-x' | 'axis-y' | 'along' | 'perp' | 'extension' | 'spacing' | 'length';

/** A dotted line to draw while a hint holds. */
export interface Track {
  a: XY;
  b: XY;
  kind: TrackKind;
  /** `a` is the point it runs from (a tracking point, a wall's end): drawn marked. */
  mark?: boolean;
}

/** Colour token per dotted line: the axes red / green, along / square magenta, an extension cyan. */
export const TRACK_TOKEN: Record<TrackKind, string> = {
  'axis-x': '--snap-axis-x',
  'axis-y': '--snap-axis-y',
  along: '--snap-parallel',
  perp: '--snap-parallel',
  extension: '--snap-midpoint',
  spacing: '--snap-parallel',
  length: '--snap-parallel',
};

/** A point tracking lines run from. */
export interface TrackPoint {
  p: XY;
  /** What it is, for the ScreenTip ("Fra endepunkt · Væg 3"). */
  what: 'endepunkt' | 'midtpunkt' | 'hjælpepunkt' | 'hjørne' | 'karm';
  /** Whose: "Væg 3"; a guide point's number. */
  of?: string;
  /** Passed by the tool (not hover-acquired): drawn only when it is used. */
  auto?: boolean;
}

/** A wall the automatic hints read: its axis and its name. */
export interface SmartWall {
  a: XY;
  b: XY;
  label: string;
}

export interface SmartContext {
  /** Hover-acquired and the tool's own tracking points. */
  tracked: readonly TrackPoint[];
  /** The walls in play (the dragged wall left out). */
  walls: readonly SmartWall[];
  axisAngle: number;
  tolerance: number;
  /** Where the segment starts: never a tracking point of its own (that is the axis lock). */
  origin: XY | null;
}

/** A direction the pointer is held to from the origin: an axis, or parallel / square to a wall. */
export interface Lock {
  o: XY;
  d: XY;
  kind: TrackKind;
}

export type SmartKind = 'track' | 'extension' | 'equal-length' | 'equal-spacing';

export interface SmartHit {
  point: XY;
  kind: SmartKind;
  tip: string;
  tracks: Track[];
}

/** How long (ms) the pointer rests on a point to acquire it (SketchUp's). */
export const HOVER_MS = 400;
/** At most this many hover-acquired points; the oldest goes first. */
export const TRACK_CAP = 4;
/** At most this many of the tool's own points. */
export const AUTO_CAP = 8;

const DEG = Math.PI / 180;
const ANGLE_TOL = 3 * DEG;
const SIN_TOL = Math.sin(ANGLE_TOL);
/** Two parallel walls closer than this are one line, not a spacing. */
const MIN_SPACING = 0.05;

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const crs = (a: XY, b: XY) => a[0] * b[1] - a[1] * b[0];
const len = (a: XY) => Math.hypot(a[0], a[1]);
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const unit = (a: XY): XY | null => {
  const L = len(a);
  return L > 1e-9 ? [a[0] / L, a[1] / L] : null;
};
const perp = (d: XY): XY => [-d[1], d[0]];
const parallel = (u: XY, v: XY) => Math.abs(crs(u, v)) < SIN_TOL;
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** How far from the pointer a hint still reaches: a few metres, more when zoomed out. */
export const reachOf = (tolerance: number) => Math.max(tolerance * 60, 4);

/** "Fra endepunkt · Væg 3", "Fra hjælpepunkt 2". */
export function trackTip(t: TrackPoint): string {
  if (!t.of) return `Fra ${t.what}`;
  return t.what === 'hjælpepunkt' ? `Fra ${t.what} ${t.of}` : `Fra ${t.what} · ${t.of}`;
}

/** A point's identity (to the millimetre): hovering the same corner from two walls is one point. */
export const trackKey = (p: XY) => `${Math.round(p[0] * 1000)},${Math.round(p[1] * 1000)}`;

// ------------------------------------------------------ hover acquiring --

export interface Tracking {
  /** Acquired, oldest first. */
  points: TrackPoint[];
  /** The point under the pointer, since when, and whether its dwell has acted yet. */
  hover: { point: TrackPoint; since: number; done: boolean } | null;
}

export const EMPTY_TRACKING: Tracking = { points: [], hover: null };

/**
 * The pointer is over `candidate` (or no point) at time `now` (ms): a
 * dwell of `HOVER_MS` acquires it — or drops it, if it was acquired —
 * once per visit. Returns `state` itself when nothing changed.
 */
export function hoverTrack(state: Tracking, candidate: TrackPoint | null, now: number): Tracking {
  if (!candidate) return state.hover ? { ...state, hover: null } : state;
  const key = trackKey(candidate.p);
  const h = state.hover;
  if (!h || trackKey(h.point.p) !== key) return { ...state, hover: { point: candidate, since: now, done: false } };
  if (h.done || now - h.since < HOVER_MS) return state;
  const had = state.points.some((q) => trackKey(q.p) === key);
  const points = had ? state.points.filter((q) => trackKey(q.p) !== key) : [...state.points, candidate].slice(-TRACK_CAP);
  return { points, hover: { ...h, done: true } };
}

/** Milliseconds until the dwell on the hovered point acts, or null when nothing is pending. */
export function dwellLeft(state: Tracking, now: number): number | null {
  const h = state.hover;
  if (!h || h.done) return null;
  return Math.max(0, HOVER_MS - (now - h.since));
}

/**
 * The tool's own tracking points: the ends of the walls being edited
 * (`guids`) and the far ends of the walls meeting them, nearest `near`
 * first, at most `AUTO_CAP`.
 */
export function autoTrackPoints(
  walls: readonly { guid: string; id: number; start: XY; end: XY }[],
  guids: readonly string[],
  near: XY | null,
  cap = AUTO_CAP,
): TrackPoint[] {
  const edited = walls.filter((w) => guids.includes(w.guid));
  const out = new Map<string, TrackPoint>();
  const put = (p: XY, of: string) => {
    const k = trackKey(p);
    if (!out.has(k)) out.set(k, { p, what: 'endepunkt', of, auto: true });
  };
  for (const w of edited) {
    put(w.start, `Væg ${w.id}`);
    put(w.end, `Væg ${w.id}`);
  }
  const at = (p: XY) => edited.some((w) => dist(w.start, p) < 0.01 || dist(w.end, p) < 0.01);
  for (const w of walls) {
    if (guids.includes(w.guid)) continue;
    if (at(w.start)) put(w.end, `Væg ${w.id}`);
    else if (at(w.end)) put(w.start, `Væg ${w.id}`);
  }
  const pts = [...out.values()];
  if (near) pts.sort((a, b) => dist(a.p, near) - dist(b.p, near));
  return pts.slice(0, cap);
}

// ------------------------------------------------------------ the lines --

/** A line a hint holds to. */
interface GuideLine {
  o: XY;
  d: XY;
  kind: Exclude<SmartKind, 'equal-length'>;
  tip: string;
  track: TrackKind;
  /** The dotted line runs from here to the hit … */
  from?: XY;
  /** … or, for a spacing, from the hit plus this (across both walls). */
  span?: XY;
}

const RANK: Record<GuideLine['kind'], number> = { track: 0, extension: 1, 'equal-spacing': 2 };

function trackOf(l: GuideLine, x: XY): Track {
  return l.span ? { a: add(x, l.span), b: x, kind: l.track } : { a: l.from ?? l.o, b: x, kind: l.track, mark: true };
}

/** The colour a direction reads in: an axis's, else the caller's. */
function dirKind(d: XY, axisAngle: number, other: TrackKind): TrackKind {
  const x: XY = [Math.cos(axisAngle), Math.sin(axisAngle)];
  if (parallel(d, x)) return 'axis-x';
  if (parallel(d, perp(x))) return 'axis-y';
  return other;
}

const offLine = (p: XY, o: XY, d: XY) => Math.abs(crs(d, sub(p, o)));

function nearestOnSeg(a: XY, b: XY, p: XY): XY {
  const d = sub(b, a);
  const L2 = dot(d, d);
  const t = L2 > 0 ? Math.max(0, Math.min(1, dot(sub(p, a), d) / L2)) : 0;
  return add(a, mul(d, t));
}

function cross(p: XY, r: XY, q: XY, s: XY): XY | null {
  const den = crs(r, s);
  if (Math.abs(den) < 1e-12) return null;
  return add(p, mul(r, crs(sub(q, p), s) / den));
}

/**
 * Every hint line within `tolerance` of `p`: tracking lines from the
 * tracked points (along the axes and, given `segDir`, along / square to
 * the segment), from the endpoints of walls parallel to `lock` (square to
 * it), the walls' extensions past their ends, and equal-spacing lines.
 */
function guideLines(p: XY, ctx: SmartContext, segDir: XY | null, lock: Lock | null): GuideLine[] {
  const tol = ctx.tolerance;
  const reach = reachOf(tol);
  const out: GuideLine[] = [];
  const ax: XY = [Math.cos(ctx.axisAngle), Math.sin(ctx.axisAngle)];
  const seen = new Set<string>();
  const fromPoint = (q: TrackPoint, dirs: readonly [XY, TrackKind][]) => {
    if (ctx.origin && dist(q.p, ctx.origin) < 1e-6) return;
    if (dist(q.p, p) > reach) return;
    for (const [d, kind] of dirs) {
      if (offLine(p, q.p, d) > tol) continue;
      // One line per point and direction, however many ways it was found.
      const k = `${trackKey(q.p)}/${Math.round(((Math.atan2(d[1], d[0]) / DEG + 360) % 180) * 10)}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ o: q.p, d, kind: 'track', tip: trackTip(q), track: kind, from: q.p });
    }
  };
  const dirs: [XY, TrackKind][] = [
    [ax, 'axis-x'],
    [perp(ax), 'axis-y'],
  ];
  if (segDir && !parallel(segDir, ax) && !parallel(segDir, perp(ax))) {
    dirs.push([segDir, 'along'], [perp(segDir), 'perp']);
  }
  for (const q of ctx.tracked) fromPoint(q, dirs);

  // Square across from the ends of the walls parallel to the lock.
  if (lock) {
    const n = perp(lock.d);
    const nk = dirKind(n, ctx.axisAngle, 'perp');
    for (const w of ctx.walls) {
      const u = unit(sub(w.b, w.a));
      if (!u || !parallel(u, lock.d)) continue;
      if (dist(nearestOnSeg(w.a, w.b, lock.o), lock.o) > reach && dist(nearestOnSeg(w.a, w.b, p), p) > reach) continue;
      for (const e of [w.a, w.b]) fromPoint({ p: e, what: 'endepunkt', of: w.label, auto: true }, [[n, nk]]);
    }
  }

  // Extensions past a wall's ends.
  for (const w of ctx.walls) {
    const d = unit(sub(w.b, w.a));
    if (!d) continue;
    const L = dist(w.a, w.b);
    const t = dot(sub(p, w.a), d);
    const beyond = t < 0 ? -t : t - L;
    if (beyond <= tol || beyond > reach || offLine(p, w.a, d) > tol) continue;
    out.push({ o: w.a, d, kind: 'extension', tip: `Forlængelse · ${w.label}`, track: 'extension', from: t < 0 ? w.a : w.b });
  }

  // The same spacing as two parallel walls: a third beyond the nearer one.
  for (const w1 of ctx.walls) {
    const d = unit(sub(w1.b, w1.a));
    if (!d || dist(nearestOnSeg(w1.a, w1.b, p), p) > reach) continue;
    const n = perp(d);
    let below: { w: SmartWall; off: number } | null = null;
    let above: { w: SmartWall; off: number } | null = null;
    for (const w2 of ctx.walls) {
      if (w2 === w1) continue;
      const u = unit(sub(w2.b, w2.a));
      if (!u || !parallel(u, d)) continue;
      const off = dot(sub(w2.a, w1.a), n);
      if (Math.abs(off) < MIN_SPACING || Math.abs(off) > reach) continue;
      if (off > 0 && (!above || off < above.off)) above = { w: w2, off };
      if (off < 0 && (!below || off > below.off)) below = { w: w2, off };
    }
    for (const nb of [below, above]) {
      if (!nb) continue;
      const o = sub(w1.a, mul(n, nb.off));
      if (offLine(p, o, d) > tol) continue;
      out.push({ o, d, kind: 'equal-spacing', tip: `Samme afstand · ${w1.label} og ${nb.w.label}`, track: 'spacing', span: mul(n, 2 * nb.off) });
    }
  }
  return out;
}

const pickBest = <T extends { rank: number; d: number }>(xs: T[]): T | null =>
  xs.reduce<T | null>((b, x) => (!b || x.rank < b.rank || (x.rank === b.rank && x.d < b.d) ? x : b), null);

/**
 * Along `lock` from its origin: where a hint line crosses it (level with
 * a tracking point, an extension, a spacing), or the length of a parallel
 * wall — within `tolerance` of `p`, the nearest of the strongest kind.
 */
export function smartOnLock(p: XY, ctx: SmartContext, lock: Lock): SmartHit | null {
  const tol = ctx.tolerance;
  const lockTrack = (x: XY): Track => ({ a: lock.o, b: x, kind: lock.kind });
  const cands: (SmartHit & { rank: number; d: number })[] = [];
  for (const l of guideLines(p, ctx, lock.d, lock)) {
    if (parallel(l.d, lock.d)) continue;
    const x = cross(lock.o, lock.d, l.o, l.d);
    if (!x) continue;
    const d = dist(x, p);
    if (d > tol) continue;
    cands.push({ point: x, kind: l.kind, tip: l.tip, tracks: [lockTrack(x), trackOf(l, x)], rank: RANK[l.kind], d });
  }
  // As long as a parallel wall nearby.
  const reach = reachOf(tol);
  const t = dot(sub(p, lock.o), lock.d);
  for (const w of ctx.walls) {
    const u = unit(sub(w.b, w.a));
    if (!u || !parallel(u, lock.d)) continue;
    if (dist(nearestOnSeg(w.a, w.b, lock.o), lock.o) > reach) continue;
    const x = add(lock.o, mul(lock.d, Math.sign(t || 1) * dist(w.a, w.b)));
    const d = dist(x, p);
    if (d > tol) continue;
    cands.push({ point: x, kind: 'equal-length', tip: `Samme længde · ${w.label}`, tracks: [lockTrack(x), { a: w.a, b: w.b, kind: 'length' }], rank: 3, d });
  }
  const best = pickBest(cands);
  return best ? { point: best.point, kind: best.kind, tip: best.tip, tracks: best.tracks } : null;
}

/**
 * Free of any lock: where two hint lines cross, else on the nearest one
 * (a tracking line before an extension before a spacing).
 */
export function smartFree(p: XY, ctx: SmartContext, segDir: XY | null): SmartHit | null {
  const lines = guideLines(p, ctx, segDir, null);
  const tol = ctx.tolerance;
  const cands: (SmartHit & { rank: number; d: number })[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    for (let j = i + 1; j < lines.length; j += 1) {
      const [a, b] = [lines[i], lines[j]];
      if (parallel(a.d, b.d)) continue;
      const x = cross(a.o, a.d, b.o, b.d);
      if (!x) continue;
      const d = dist(x, p);
      if (d > tol) continue;
      const kind = a.kind === 'track' || b.kind === 'track' ? 'track' : a.kind;
      cands.push({ point: x, kind, tip: `${a.tip} · ${lowerFirst(b.tip)}`, tracks: [trackOf(a, x), trackOf(b, x)], rank: 0, d });
    }
  }
  const pair = pickBest(cands);
  if (pair) return { point: pair.point, kind: pair.kind, tip: pair.tip, tracks: pair.tracks };
  const single = pickBest(
    lines.map((l) => {
      const x = add(l.o, mul(l.d, dot(sub(p, l.o), l.d)));
      return { l, x, rank: RANK[l.kind], d: dist(x, p) };
    }),
  );
  return single ? { point: single.x, kind: single.l.kind, tip: single.l.tip, tracks: [trackOf(single.l, single.x)] } : null;
}

/**
 * On segment ab (a wall's face, a trace, a guide): where a hint line
 * crosses it within `tolerance` of `p` — the point stays on the segment.
 */
export function smartOnSegment(p: XY, ctx: SmartContext, a: XY, b: XY, segDir: XY | null): SmartHit | null {
  const d = unit(sub(b, a));
  if (!d) return null;
  const L = dist(a, b);
  const cands: (SmartHit & { rank: number; d: number })[] = [];
  for (const l of guideLines(p, ctx, segDir, null)) {
    if (parallel(l.d, d)) continue;
    const x = cross(a, d, l.o, l.d);
    if (!x) continue;
    const t = dot(sub(x, a), d);
    if (t < -1e-9 || t > L + 1e-9) continue;
    const dd = dist(x, p);
    if (dd > ctx.tolerance) continue;
    cands.push({ point: x, kind: l.kind, tip: l.tip, tracks: [trackOf(l, x)], rank: RANK[l.kind], d: dd });
  }
  const best = pickBest(cands);
  return best ? { point: best.point, kind: best.kind, tip: best.tip, tracks: best.tracks } : null;
}

// ------------------------------------------------- one-dimensional hints --

/**
 * A wall moved along its normal: the offset (from the moving line's start)
 * that spaces it evenly — halfway between two parallel lines, or as far
 * beyond one as the next is — within `tolerance` of `raw`. `lines` are the
 * parallel walls' offsets on the same normal.
 */
export function spacingOffset(lines: readonly { offset: number; label: string }[], raw: number, tolerance: number): { offset: number; tip: string; from: number; to: number } | null {
  const xs = [...lines].sort((a, b) => a.offset - b.offset);
  let best: { offset: number; tip: string; from: number; to: number; d: number } | null = null;
  const consider = (offset: number, tip: string, from: number, to: number) => {
    const d = Math.abs(offset - raw);
    if (d <= tolerance && (!best || d < best.d)) best = { offset, tip, from, to, d };
  };
  for (let i = 0; i + 1 < xs.length; i += 1) {
    const [lo, hi] = [xs[i], xs[i + 1]];
    const gap = hi.offset - lo.offset;
    if (gap < MIN_SPACING) continue;
    consider((lo.offset + hi.offset) / 2, `Samme afstand · ${lo.label} og ${hi.label}`, lo.offset, hi.offset);
    consider(hi.offset + gap, `Samme afstand · ${hi.label} og ${lo.label}`, lo.offset, hi.offset + gap);
    consider(lo.offset - gap, `Samme afstand · ${lo.label} og ${hi.label}`, lo.offset - gap, hi.offset);
  }
  if (!best) return null;
  const { offset, tip, from, to } = best;
  return { offset, tip, from, to };
}

/**
 * Something sliding along a line (an opening on its host): the position
 * `s` (metres along unit `d` from `o`) where one of its `marks` (offsets
 * from `s`: the middle 0, the jambs ±width/2) is level with a tracking
 * point — square across from it — within `tolerance` of the pointer's.
 */
export function alignAlong(
  o: XY,
  d: XY,
  s: number,
  marks: readonly number[],
  points: readonly TrackPoint[],
  tolerance: number,
): { s: number; tip: string; track: Track } | null {
  const reach = reachOf(tolerance);
  let best: { s: number; tip: string; track: Track; gap: number } | null = null;
  for (const q of points) {
    const sq = dot(sub(q.p, o), d);
    const foot = add(o, mul(d, sq));
    if (dist(foot, q.p) > reach || dist(foot, q.p) < 1e-6) continue;
    for (const m of marks) {
      const target = sq - m;
      const gap = Math.abs(target - s);
      if (gap <= tolerance && (!best || gap < best.gap)) best = { s: target, tip: trackTip(q), track: { a: q.p, b: foot, kind: 'perp', mark: true }, gap };
    }
  }
  if (!best) return null;
  const { s: at, tip, track } = best;
  return { s: at, tip, track };
}
